import React, { useState, useEffect, useRef } from 'react';
import './ActivityFloatingBox.css';

// 湖北专利运用公共服务平台链接
const EXTERNAL_LINK = 'https://zscqj.hubei.gov.cn/hbPatentPublic/';

interface ActivityFloatingBoxProps {
  visible: boolean;
  onClose: () => void;
  activityData?: {
    id: number;
    title: string;
    publishTime: string;
  };
}

const ActivityFloatingBox: React.FC<ActivityFloatingBoxProps> = ({
  visible,
  onClose,
  activityData
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 20, y: window.innerHeight / 2 });
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false);
  const [hasMoved, setHasMoved] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  const animationFrameRef = useRef<number>();

  // 获取组件尺寸 - 根据图片自适应
  const getBoxSize = () => {
    return { width: 240, height: 160 }; // 默认尺寸，实际由图片决定
  };

  // 格式化日期为中文格式：2025年10月30日
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return dateString; // 如果日期无效，返回原字符串
      }
      
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const day = date.getDate();
      
      return `${year}年${month}月${day}日`;
    } catch (error) {
      console.error('日期格式化失败:', error);
      return dateString; // 出错时返回原字符串
    }
  };

  // 球体式不规则运动动画
  useEffect(() => {
    // 当鼠标悬停或正在拖动时，停止动画
    if (!visible || isDragging || isHovered) return;

    const boxSize = getBoxSize();
    let currentVelocity = { x: 0.3, y: 0.4 }; // 初始速度向量
    let lastTime = Date.now();

    const animate = () => {
      const now = Date.now();
      const deltaTime = (now - lastTime) / 16; // 标准化到60fps
      lastTime = now;

      const maxX = window.innerWidth - boxSize.width - 20;
      const maxY = window.innerHeight - boxSize.height - 20;
      const minX = 20;
      const minY = 20;

      // 使用时间作为参数创建不规则的速度变化
      const time = now / 1000;
      const speedModifierX = Math.sin(time * 0.3) * 0.5 + Math.cos(time * 0.5) * 0.3 + 0.8;
      const speedModifierY = Math.cos(time * 0.4) * 0.5 + Math.sin(time * 0.6) * 0.3 + 0.8;
      
      // 更新速度向量，添加随机性
      currentVelocity.x += (Math.sin(time * 0.7) * 0.02 - currentVelocity.x * 0.01);
      currentVelocity.y += (Math.cos(time * 0.8) * 0.02 - currentVelocity.y * 0.01);
      
      // 应用速度修改器
      let vx = currentVelocity.x * speedModifierX;
      let vy = currentVelocity.y * speedModifierY;
      
      // 限制速度范围
      const maxSpeed = 1.5;
      vx = Math.max(-maxSpeed, Math.min(maxSpeed, vx));
      vy = Math.max(-maxSpeed, Math.min(maxSpeed, vy));
      
      // 计算新位置
      setPosition(prev => {
        let newX = prev.x + vx * deltaTime;
        let newY = prev.y + vy * deltaTime;
        
        // 边界检测和反弹（翻转速度）
        if (newX <= minX) {
          currentVelocity.x = Math.abs(currentVelocity.x);
          newX = minX;
        } else if (newX >= maxX) {
          currentVelocity.x = -Math.abs(currentVelocity.x);
          newX = maxX;
        }
        
        if (newY <= minY) {
          currentVelocity.y = Math.abs(currentVelocity.y);
          newY = minY;
        } else if (newY >= maxY) {
          currentVelocity.y = -Math.abs(currentVelocity.y);
          newY = maxY;
        }
        
        return { x: newX, y: newY };
      });

      if (visible && !isDragging && !isHovered) {
        animationFrameRef.current = requestAnimationFrame(animate);
      }
    };

    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [visible, isDragging, isHovered]);

  // 拖拽处理 - 整个悬浮框都可以拖拽
  const handleMouseDown = (e: React.MouseEvent) => {
    // 如果是关闭按钮点击，不触发拖拽
    if ((e.target as HTMLElement).closest('button.close-btn')) {
      return;
    }
    
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    setHasMoved(false); // 重置移动标记
    
    const rect = boxRef.current?.getBoundingClientRect();
    if (rect) {
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
    }
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    // 标记已经移动过
    setHasMoved(true);
    
    const boxSize = getBoxSize();
    const newX = e.clientX - dragOffset.x;
    const newY = e.clientY - dragOffset.y;
    
    // 限制在浏览器窗口可见范围内
    const maxX = window.innerWidth - boxSize.width - 20;
    const maxY = window.innerHeight - boxSize.height - 20;
    const minX = 20;
    const minY = 20;
    
    const constrainedX = Math.max(minX, Math.min(newX, maxX));
    const constrainedY = Math.max(minY, Math.min(newY, maxY));
    
    // 使用 requestAnimationFrame 确保流畅动画
    requestAnimationFrame(() => {
      setPosition({ x: constrainedX, y: constrainedY });
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    // 延迟重置移动标记，避免立即触发点击
    setTimeout(() => {
      setHasMoved(false);
    }, 100);
  };

  // 全局鼠标事件监听
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'grabbing';
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    }
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, [isDragging, dragOffset]);

  // 窗口大小改变时调整位置
  useEffect(() => {
    const handleResize = () => {
      const boxSize = getBoxSize();
      const maxX = window.innerWidth - boxSize.width - 20;
      const maxY = window.innerHeight - boxSize.height - 20;
      const minX = 20;
      const minY = 20;
      
      setPosition(prev => ({
        x: Math.max(minX, Math.min(prev.x, maxX)),
        y: Math.max(minY, Math.min(prev.y, maxY))
      }));
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);


  if (!visible) return null;

  return (
    <div
      ref={boxRef}
      className={`activity-floating-box ${isDragging ? 'dragging' : ''} ${isHovered ? 'hovered' : ''}`}
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        cursor: isDragging ? 'grabbing' : 'pointer',
        width: '240px',
        height: 'auto',
        padding: 0,
        background: 'transparent',
        boxShadow: 'none',
        border: 'none',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onMouseDown={handleMouseDown}
    >
      {/* 关闭按钮 */}
      <button 
        className="close-btn"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        title="关闭"
        style={{
          position: 'absolute',
          top: '8px',
          right: '8px',
          zIndex: 10,
        }}
      >
        ×
      </button>
      
      {/* 图片内容 */}
      <img
        src="/images/float.png"
        alt="智慧桥平台"
        style={{
          width: '100%',
          height: 'auto',
          display: 'block',
          borderRadius: '12px',
          cursor: 'pointer',
        }}
        onClick={(e) => {
          // 如果刚刚完成拖拽（有移动），不触发点击
          if (hasMoved || isDragging) {
            return;
          }
          e.stopPropagation();
          window.open(EXTERNAL_LINK, '_blank', 'noopener,noreferrer');
        }}
      />
    </div>
  );
};

export default ActivityFloatingBox;
